import {
  addDays,
  eachDay,
  format,
  isAfter,
  differenceInCalendarDays,
} from "date-fns";
import { map } from "ramda";

import { dateToWeekday } from "helpers/dates";
import { IShiftWithMergedDate } from "helpers/transforms/schedule";
import { Frequency, IUnavailabilityBlock } from "types/schedules";
import { Maybe } from "true-myth";

export type TimeBoundary = "start" | "end";

export function splitShiftDate(time: Date, timeBoundary: TimeBoundary) {
  const timeHour = `${timeBoundary}TimeHour`;
  const timeMinute = `${timeBoundary}TimeMinute`;
  return {
    [timeHour]: parseInt(format(time, "H"), 10),
    [timeMinute]: parseInt(format(time, "m"), 10),
  };
}

export function buildShiftDate(
  typedShift: IShiftWithMergedDate,
  timeBoundary: TimeBoundary
): Date {
  const {
    startTimeHour,
    startTimeMinute,
    endTimeHour,
    endTimeMinute,
  } = typedShift;

  const [year, month, day] = map(
    datePart => parseInt(datePart, 10),
    typedShift.date.split("-")
  );

  const startTime = new Date(
    year,
    month - 1,
    day,
    startTimeHour,
    startTimeMinute
  );
  if (timeBoundary === "end") {
    const endTime = new Date(year, month - 1, day, endTimeHour, endTimeMinute);

    return !isAfter(endTime, startTime) ? addDays(endTime, 1) : endTime;
  } else {
    return startTime;
  }
}

export function syncUnavailsToDateRange(
  unavailBlocks: IUnavailabilityBlock[],
  startDate: Maybe<Date>,
  endDate: Maybe<Date>
): IUnavailabilityBlock[] {
  if (!unavailBlocks.length) {
    return unavailBlocks;
  }
  return Maybe.all(startDate, endDate).match({
    Just: ([start, end]) => {
      const datesInSchedule = eachDay(start, end);
      return unavailBlocks.map(unavail => {
        if (unavail.frequency === Frequency.WEEKLY) {
          const matchingDay = datesInSchedule.find(
            day => dateToWeekday(day) === unavail.dayOfWeek
          );
          if (matchingDay) {
            const daysDifference = differenceInCalendarDays(
              matchingDay,
              unavail.startTime
            );
            return {
              ...unavail,
              startTime: addDays(unavail.startTime, daysDifference),
              endTime: addDays(unavail.endTime, daysDifference),
            };
          }
        }
        return unavail;
      });
    },
    Nothing: () => unavailBlocks,
  });
}
